// Web Push (Firebase Cloud Messaging) helper for the Mayday beneficiary app.
// Wraps notification permission, service-worker registration and FCM token
// retrieval so the beneficiary can be alerted of an assistance message even
// when the chat tab is backgrounded. Everything degrades to a no-op when the
// browser does not support Web Push (e.g. iOS Safari outside a PWA install).
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, onMessage } from 'firebase/messaging'
import { firebaseConfig } from '@config/firebase'
import { debugMessage } from '@lib/utilities'

// Must match the file served at the site root (public/firebase-messaging-sw.js)
// so the service worker scope covers the whole app.
const SERVICE_WORKER_PATH = '/firebase-messaging-sw.js'

// Lazily-built FCM messaging instance, shared across calls.
let messagingInstance = null

// True when the browser exposes everything Web Push needs.
export const supported = () =>
  'serviceWorker' in navigator &&
  'Notification' in window &&
  'PushManager' in window

// Current Web Push permission as a single status the UI branches on:
// 'granted' | 'denied' | 'default' | 'unsupported'. 'default' is the only
// state where prompting helps — and on mobile that prompt must be triggered
// from a user gesture, hence the explicit enable button in the UI.
export const permissionStatus = () =>
  supported() ? Notification.permission : 'unsupported'

const messaging = () => {
  if (messagingInstance === null) {
    messagingInstance = getMessaging(initializeApp(firebaseConfig))
  }
  return messagingInstance
}

export const registerServiceWorker = () =>
  navigator.serviceWorker.register(SERVICE_WORKER_PATH)

// Requests notification permission and, once granted, registers the service
// worker and resolves the FCM token (handed to `onToken`). Returns a status
// string — 'granted' | 'denied' | 'dismissed' | 'unsupported' | 'error' —
// so the caller can react without catching exceptions.
export const enableWebPush = async ({ vapidKey, onToken } = {}) => {
  if (!supported()) {
    return 'unsupported'
  }

  let permission
  try {
    permission = await Notification.requestPermission()
  } catch (error) {
    // Legacy Safari only exposed the callback form of requestPermission;
    // treat a throw as the user not granting permission.
    debugMessage(`[WebPush] requestPermission threw: ${error}`, 'beneficiaryChannel')
    return 'dismissed'
  }

  if (permission !== 'granted') {
    // Both mean "no token", but differ on what a later retry does:
    // - 'denied' = explicit refusal, persisted by the browser. A future
    //   requestPermission() returns 'denied' immediately without re-prompting,
    //   so retrying the setup is a silent no-op.
    // - 'dismissed' ('default') = prompt closed without choosing, NOT persisted.
    //   A later reconnection can prompt again, giving the user another chance.
    return permission === 'denied' ? 'denied' : 'dismissed'
  }

  try {
    const serviceWorkerRegistration = await registerServiceWorker()
    const token = await getToken(messaging(), { vapidKey, serviceWorkerRegistration })

    if (token && typeof onToken === 'function') {
      onToken(token)
    }
    return 'granted'
  } catch (error) {
    debugMessage(`[WebPush] token registration failed: ${error}`, 'beneficiaryChannel')
    return 'error'
  }
}

// Foreground messages: the service worker's onBackgroundMessage does NOT fire
// while the tab is focused, so register an in-app handler here if needed.
export const onForegroundMessage = (handler) => {
  if (!supported()) {
    return
  }
  onMessage(messaging(), handler)
}
